<template>
    <div>
        <h1 class="mx-3 mt-3">Binance</h1>

        <rounded-container variant="dark">
            <template v-slot:title>
                Binance orders <span v-if="binance.openOrders.length" class="text-secondary">{{ binance.openOrders.length }} open</span>
            </template>

            <div v-if="loading" class="text-center p-3">
                <b-spinner small type="grow" variant="success"></b-spinner>
            </div>

            <b-container v-else-if="binance.openOrders.length" class="p-0">
                <b-row v-for="(order, rowIndex) in binance.openOrders" :key="order.orderId" :class="rowClass(rowIndex)" class="px-3 py-2">
                    <b-col>
                        <span v-if="order.side === 'SELL'" class="text-danger">{{ order.symbol }}</span>
                        <span v-if="order.side === 'BUY'" class="text-success">{{ order.symbol }}</span>
                    </b-col>
                    <b-col>
                        <span v-if="(order.price * order.origQty) > 50.5">${{ formatPrice(order.price * order.origQty) }}</span>
                        <span v-else class="text-secondary">${{ formatPrice(order.price * order.origQty) }}</span>
                    </b-col>
                    <b-col>${{ formatPrice(parseFloat(order.price)) }}</b-col>
                    <b-col>{{ formatTime(new Date(order.updateTime)) }}</b-col>
                </b-row>
            </b-container>
            <b-container v-else class="text-center text-secondary p-5">
                No open orders.
            </b-container>
        </rounded-container>

        <rounded-container variant="dark">
            <template v-slot:title>
                Binance holdings<span v-if="binance.accountValue"> - <strong>${{ formatHoldings(binance.accountValue) }}</strong> account value</span>
            </template>

            <div v-if="loading" class="text-center p-3">
                <b-spinner small type="grow" variant="success"></b-spinner>
            </div>

            <b-container v-else-if="holdingRows.length" class="p-0">
                <b-row class="px-3 py-2 text-secondary">
                    <b-col cols=4><strong>asset</strong></b-col>
                    <b-col><strong>holdings</strong></b-col>
                    <b-col><strong>value</strong></b-col>
                </b-row>

                <b-row v-for="(balance, rowIndex) in holdingRows" :key="balance.symbol" :class="[rowClass(rowIndex), canLongClass(balance)]" class="px-3 py-2 text-secondary">
                    <b-col class="no-bold" cols=4>
                        <span v-if="'asset' in balance">
                            <asset-symbol :asset="balance.asset" />
                        </span>
                        <span v-else>{{ balance.symbol }}</span>
                    </b-col>

                    <b-col class="locked-amount">
                        {{ formatHoldings(balance.holdings) }}
                    </b-col>

                    <b-col :class="{ 'text-white': 'asset' in balance }">
                        <span v-if="balance.value !== null">
                            ${{ formatNumber(balance.value) }}
                        </span>
                        <span v-else>
                            N/A
                        </span>
                    </b-col>
                </b-row>
            </b-container>
            <b-container v-else-if="apiError" class="text-center p-5">
                {{ apiError }}
            </b-container>
            <b-container v-else class="text-center text-secondary p-5">
                No balances found.
            </b-container>
        </rounded-container>

        <bucket-sync
            v-if="!loading"
            endpoint="/api/binance/sync-bucket"
            label="Binance"
            :last-check="binance.bucketLastCheck"
        />
    </div>
</template>
<script>

import apiMixin from '@/mixins/api';
import formatter from '../mixins/formatter';
import RoundedContainer from '../components/style/RoundedContainer.vue';
import AssetSymbol from '../components/style/AssetSymbol.vue';
import BucketSync from '../components/widgets/BucketSync.vue';

export default {
    name: 'Binance',
    mixins: [apiMixin, formatter],
    components: { RoundedContainer, AssetSymbol, BucketSync },
    data() {
        return {
            loading: true,
            binance: {
                accountValue: null,
                balances: [],
                openOrders: [],
                bucketLastCheck: null,
            },
            apiError: null,
        }
    },
    created() {
        this.retrieveBinanceClient()
    },
    methods: {
        async retrieveBinanceClient() {
            const response = await this.apiGet('/api/binance-client');

            this.loading = false;
            if (!response) return;

            this.apiError = response.data.api_error || null;
            this.binance.accountValue = response.data.account_value;
            this.binance.openOrders = response.data.open_orders.sort((a, b) => (a.updateTime < b.updateTime) ? 1 : -1);
            this.binance.balances = response.data.balances;
            this.binance.bucketLastCheck = response.data.bucket_last_check;
        },
        canLongClass(balance) {
            if (!balance.asset?.meta?.canLong) return '';
            if (balance.surplus_value > 50) return 'highlight-row';

            return '';
        }
    },
    computed: {
        holdingRows() {
            /* The API prices a holding from Warren, from a stablecoin peg or from the
               Binance ticker. A coin it can price none of those ways carries no value. */
            return this.binance.balances
                .map(balance => ({
                    ...balance,
                    value: balance.price === null ? null : balance.holdings * balance.price,
                }))
                .sort((a, b) => (a.value < b.value) ? 1 : -1);
        },
    }
}
</script>
<style scoped>
.highlight-row {
    font-weight: bold;
}
.highlight-row .locked-amount {
    color: #fff;
}
.no-bold {
    font-weight: normal;
}
</style>